<template>
  <section class="promo__codes">
    <TableCard
      ref="datatable"
      v-model:sort="sorting"
      v-model:page="page"
      v-model:page-size="pageSize"
      :loading="loading"
      :columns="columns"
      :sort-by-name="sortByName"
      :sort-dir-name="sortDirName"
      :actions="actions"
      :rows="rows"
      :count="promocode.totalRows"
      :show-total="false"
      :row-selectable="false"
      dont-fill-empty
      extend-height
      tablet-layout
      i18n-path="reports"
      @fetch-data="fetchDataTable"
    >
      <template v-slot:header>
        <TableCardSearchHeader
          v-model="search"
          class="ui-g-md"
          show-actions-mobile
          @search="handleSearch"
        >
          <template v-slot:actions>
            <UiButton
              icon="refresh"
              @click="refresh"
            />
          </template>
        </TableCardSearchHeader>
      </template>


      <template v-slot:actionsPrepend>
        <div class="ui-g-md ui-mobile-hide">
          <UiButton
            icon="refresh"
            class="btn btn-lowercase "
            @click="refresh"
          >
            <span class="ml-4">{{ $t('filters.refresh') }}</span>
          </UiButton>
        </div>
        <Tooltip
          class="ui-g-md tooltip-btn-disabled"
          :title="$t('tooltip_limits.promoCodes')"
          placement="bottom-left"
          :disabled="!addDisabled"
        >
          <button
            v-if="!isEmptyOffers && iCreatePromoCode"
            class="btn btn-primary btn-sm ui-mobile-12"
            :disabled="addDisabled"
            @click="addCode"
          >
            + {{ $t('promoCodes.promocodes.addPromocode') }}
          </button>
        </Tooltip>
      </template>
    </TableCard>


    <AddPromoCode
      ref="addCode"
      :offset-params="{
        ...queryParams,
        sort_column,
        sort_dir,
        limit: pageSize,
        search: search,
        media_buying_campaign_id: 'null'
      }"
    />

    <EditPromoCode
      ref="editCode"
      :offset-params="{
        ...queryParams,
        sort_column,
        sort_dir,
        limit: pageSize,
        search: search,
        media_buying_campaign_id: 'null'
      }"
    />
  </section>
</template>

<script>
import moment from 'moment';
import UrlMixin from '@/components/Query/url-mixin';
import UrlMixinHooks from '@/components/Query/url-mixin-hooks';
import InjectPlugins from '@/components/Report/Components/inject-plugins';
import TableCard from '@/components/Cards/TableCard.vue';
import TableCardSearchHeader from '@/components/Cards/TableCardSearchHeader.vue';
import HelpersMixin from '@/components/Helpers/helpers-mixin';
import { pageSizeMixin, resolvePageSize } from '@/views/Dashboard/Reports/page_size';
import tableFlowMixinGenerator from '@/views/Dashboard/Reports/tableFlowMixin';
import Tooltip from '@/components/Tooltip.vue';
import promocodeState from '@/store/promocode/state';
import UiTableAction from '@/components/ui/UiTableAction.vue';
import UiTablePopoverAction from '@/components/ui/UiTablePopoverAction.vue';
import EditPromoCode from './PromoCodes/EditPromoCode.vue';
import AddPromoCode from './PromoCodes/AddPromoCode.vue';

const sizes = {
  1080: 20,
  1440: 30,
};
const viewName = '/promo-codes';
const pageLimitProp = 'pageSize';
const pageSize = resolvePageSize(viewName, {
  _default: 15,
  sizes,
});

const defaultParams = {
  totalRows: 0,
  search: '',
  page: 1,
  pageSize,
  sort_column: 'created_at',
  sort_dir: 'desc',
};

const fetch = async (ctx, params) => {
  if (ctx.$_.isEmpty(params)) {
    params = {
      ...defaultParams,
    };
  }

  const trafficSources = () => ctx.$store.dispatch('getTrafficSources', {});
  const promocodeOffers = () => ctx.$store.dispatch('getPromocodeOffers');
  const count = () => ctx.$store.dispatch('getPromocodesCount', { media_buying_campaign_id: 'null' });
  const promocodes = () => ctx.$store.dispatch('getPromocodes', {
    sort_column: params.sort_column || defaultParams.sort_column,
    sort_dir: params.sort_dir || defaultParams.sort_dir,
    search: params.search || '',
    limit: ctx.pageSize || pageSize,
    media_buying_campaign_id: 'null',
    ...params,
  });

  const result = await Promise.all([trafficSources(), promocodeOffers(), count(), promocodes()]);

  return [result[2], result[3]];
};

const tableFlowMixin = tableFlowMixinGenerator({
  defaultParams: {
    ...defaultParams,
  },
  params: Object.keys(defaultParams),
  fetch,
  windowRefreshHook: true,
  initPeriod: 'init',
  sortByName: 'sort_column',
  sortDirName: 'sort_dir',
});


export default {
  name: 'PromoCodes',
  fetch,
  components: {
    AddPromoCode,
    EditPromoCode,
    TableCard,
    TableCardSearchHeader,
    Tooltip,
  },
  mixins: [
    UrlMixin,
    UrlMixinHooks,
    InjectPlugins,
    HelpersMixin,
    pageSizeMixin,
    tableFlowMixin,
  ],
  props: ['fetchedData', 'passedParams'],
  data() {
    return {
      viewName,
      pageLimitProp,
      loading: false,
      columns: [
        {
          name: 'created_at',
          align: 'left',
          sortable: 'custom',
          filterable: false,
          width: 155,
          format: 'date-time',
          source: 'i18n',
          i18nPath: 'reports',
        }, {
          name: 'promo_code',
          align: 'left',
          filterable: false,
          width: 300,
          sortable: false,
        }, {
          name: 'registrations',
          align: 'center',
          filterable: false,
          sortable: false,
        }, {
          name: 'players',
          align: 'center',
          filterable: false,
          sortable: false,
        },
      ],
      lastQuery: {},
      isTableReady: false,
      // vue3-migration -- убираем перерисовку в фоне для keep-alive
      promocode: promocodeState(),
    };
  },
  computed: {
    computedPromocodeStoreParams() {
      return {
        activated: this.isComponentActivated,
        promocode: this.$store.state.promocode,
      };
    },
    isEmptyOffers() {
      return this.$_.isEmpty(this.promocode.offers);
    },
    iCreatePromoCode() {
      return this.$store.state.settings.limitations.allow_promo_code_create_or_edit;
    },

    rows() {
      return this.promocode.promocodes.map((e) => {
        const subids = [];
        for (let i = 1; i <= 5; i++) {
          const key = `subid${i}`;
          subids.push({ value: e[key] });
        }

        let tail = false;
        while (!tail) {
          const item = subids.pop();

          if (item === undefined) {
            subids.push({ value: null });
            break;
          }
          tail = item.value;
          if (tail) subids.push(item);
        }

        return {
          data: {
            id: e.id,
            affiliateOfferId: e.affiliate_offer_id,
            subids,
            traffic_source_id: e.traffic_source_id,
          },
          promo_code: e.promo_code,
          registrations: e.registrations,
          players: e.players,
          created_at: e.created_at,
        };
      });
    },
    addDisabled() {
      const { promo_codes_max_count } = this.$store.state.settings.limitations;
      return this.$store.state.promocode.count >= promo_codes_max_count;
    },
    actions() {
      const operations = [
        {
          name: 'statistics',
          componentAlt: {
            component: UiTableAction,
            props: (row) => {
              const urlQuery = {};
              urlQuery.openPromocodeReport = row;
              urlQuery.passedPeriod = {
                from: moment()
                  .startOf('month')
                  .format('YYYY-MM-DD 00:00:00'),
                to: moment()
                  .endOf('day')
                  .format('YYYY-MM-DD 23:59:59'),
                period: 30,
              };

              const compressedUrlQuery = this.compressUrlQuery(urlQuery);

              return {
                tooltipContent: this.$t('promoCodes.promocodes.statistics'),
                iconClass: 'far fa-chart-bar',
                colorPreset: 'info',
                link: `#/dashboard/custom/${compressedUrlQuery}`,
                onClick: (e) => {
                  e.preventDefault();
                  this.$router.push({
                    name: 'routes.custom',
                    params: { urlQuery: compressedUrlQuery },
                  });
                },
              };
            },
          },
        },
        !this.isEmptyOffers && {
          name: 'edit',
          componentAlt: {
            component: UiTableAction,
            props: (row) => ({
              tooltipContent: this.$t('postbacks.card.edit'),
              iconClass: 'far fa-pen',
              colorPreset: 'green',
              onClick: () => {
                this.editCode({
                  promo_code: row.promo_code,
                  affiliateOfferId: row.data.affiliateOfferId,
                  id: row.data.id,
                  traffic_source_id: row.data.traffic_source_id,
                  subids: row.data.subids,
                });
              },
            }),
          },
        },
        {
          name: 'archive',
          componentAlt: {
            component: UiTablePopoverAction,
            props: (row) => ({
              tooltipContent: this.$t('promoCodes.promocodes.delete'),
              iconClass: 'far fa-trash-alt',
              colorPreset: 'red',
              width: 250,
              message: this.$t('mediaBuying.campaigns.delete_promocode_message'),
              buttons: [{
                text: this.$t('mediaBuying.channels.cancel'),
              }, {
                text: this.$t('promoCodes.promocodes.delete'),
                type: 'danger',
                click: () => this.deleteKey(row.data.id),
              }],
            }),
          },
        },
      ];

      return {
        width: this.isEmptyOffers ? 80 : 120,
        name: () => this.$t('profile.apiKeys.table.actions'),
        operations: operations.filter((item) => !!item),
      };
    },
  },
  watch: {
    computedPromocodeStoreParams: {
      immediate: true,
      deep: true,
      handler({activated, promocode}) {
        if (activated) {
          this.promocode = this.$_.cloneDeep(promocode);
        }
      },
    },
  },
  mounted() {},

  created() {
    this.actions.operations.map((e) => {
      if (e.component) {
        e.component = e.component.bind(this);
      }
    });
    this.setFromQuery(this.passedParams);
  },

  activated() {},

  methods: {
    async getPromoCodes(data = {}) {
      this.loading = true;
      await this.$store.dispatch('getPromocodesCount', { media_buying_campaign_id: 'null' });
      return await this.$store.dispatch('getPromocodes', {
        ...this.queryParams,
        limit: this.pageSize,
        ...this.period,
        ...data,
        ...this.sorting,
        media_buying_campaign_id: 'null',
      })
        .then(() => {
          setTimeout(() => this.loading = false, 800);
        });
    },
    deleteKey(name) {
      this.$store
        .dispatch('deletePromoCode', name)
        .then(() => {
          if (this.rows.length === 1 && this.page > 1) {
            this.page = this.page - 1;
          }

          this.getPromoCodes()
            .then(() => {
              this.$alert.success(this.$t('promoCodes.alerts.promocodeDeleted'));
            });
        })
        .catch(() => {
          this.$alert.error(this.$t('promoCodes.alerts.unexpectedError'));
        });
    },
    addCode() {
      if (!this.addDisabled) {
        this.$refs.addCode.open();
      }
    },
    editCode(promocode) {
      this.$refs.editCode.open(promocode);
    },
  },
};
</script>

<style lang="scss">
// vue3-migration -- '@/assets/theme/common-page-styles.scss'
// @import 'src/assets/theme/default/promocodes/promocodes';

.promo__codes {
  .table-card-search-header__content {
    flex-wrap: nowrap;
  }
  .ui-tooltip__title-host.bottom-left {
    bottom: -4px;
  }
}
</style>
